import { loadStripe } from '@stripe/stripe-js';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderAPI } from '../../apis/order-api';
import { usePublicAPI } from '../../apis/public-api';
import { NewPaymentCardModalBody } from '../../components/payment/new-payment-card-modal-body';
import { NewPaymentCardModalTitle } from '../../components/payment/new-payment-card-modal-title';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useFeature } from '../../hooks/use-feature';
import { usePaymentInterval } from '../../hooks/use-payment-interval';
import { useShake } from '../../hooks/use-shake';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { layoutDefaults } from '../../utils/layout-defaults';
import { PaymentType } from '../../utils/manager/card-manager';
import { usePaymentCardManager } from '../../utils/manager/payment-card-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function useNewPaymentCardModal() {
  const storage = useStorage();
  const {
    spot,
    setPaying,
    setOrders,
    menuLayout,
    config,
    paymentRef,
    menuInfo,
  } = storage;
  const feature = useFeature();
  const { getPayCardPrivacyPolicyFromRemote } = usePublicAPI();
  const { generateAndStorePaymentCard, cancelPaymentCard, refreshPaymentCard } =
    usePaymentCardManager();
  const { handleErrorOnModal } = useErrorHandler();
  const analytics = useAnalytics();
  const confirmButtonTextRef = useRef();
  const chargeRef = useRef();
  const isUnderErrorRef = useRef();
  const { analyticsPageView } = analytics;
  const { findSpotOrdersFromRemote } = useOrderAPI();
  const { swal } = useSwal({ storage });
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const ordersRef = useRef();
  const tipAmountRef = useRef();
  const stripePromiseRef = useRef();
  const wasCancelledRef = useRef(false);
  const wasPaidRef = useRef(false);

  const onPaymentPaid = async () => {
    wasPaidRef.current = true;
    swal.close();
  };

  const onPaymentCancelled = async () => {
    wasCancelledRef.current = true;
    swal.close();
  };

  usePaymentInterval(
    {
      payment: paymentRef.current,
      orders: ordersRef.current,
      onPaymentPaid,
      onPaymentCancelled,
    },
    focused && !loading,
    [focused, loading]
  );

  const didOpen = () => {
    analyticsPageView('Payment Card Detail');
    setPaying(true);
    setFocused(true);
  };

  const didDestroy = () => {
    setPaying(false);
    setFocused(false);
  };

  const handleOnLoad = ({ charge }) => {
    chargeRef.current = charge;
  };

  useShake({
    element: confirmButtonTextRef?.current?.parentElement,
  });

  const handleOnError = (error, handleGeneric) => {
    isUnderErrorRef.current = true;

    if (!_.isEmpty(error)) {
      handleErrorOnModal(error, handleGeneric);
    }
  };

  async function prepare({ orders, tipAmount }) {
    try {
      ordersRef.current = orders;
      tipAmountRef.current = tipAmount;
      stripePromiseRef.current = await loadStripe(config?.stripe?.api_key);

      if (
        paymentRef.current == null ||
        (paymentRef.current === 'paying' && _.isEmpty(paymentRef.current.card))
      ) {
        await generateAndStorePaymentCard({
          orders,
          tipAmount,
          type: PaymentType.CARD,
        });
      }
    } catch (error) {
      handleErrorOnModal(error);
    }

    return null;
  }

  async function show(
    { showBackButton, prepareOnPreConfirm, prepareOnPreDeny } = {
      showBackButton: false,
    }
  ) {
    let wasSucccess = false;

    const result = await swal.fire({
      title: (
        <NewPaymentCardModalTitle
          style={{
            marginTop: layoutDefaults.biggerVerticalMargin,
          }}
          title={
            showBackButton
              ? t('new-card-payment.modal.title')
              : t('card-payment.modal.title')
          }
          analytics={analytics}
          getPrivacyPolicyFromRemote={getPayCardPrivacyPolicyFromRemote}
        />
      ),
      html: (
        <NewPaymentCardModalBody
          spot={spot}
          stripePromise={stripePromiseRef.current}
          menuInfo={menuInfo}
          menuLayout={menuLayout}
          clientSecret={paymentRef.current?.card.client_secret}
          intentId={paymentRef.current?.card.intent_id}
          payment={paymentRef.current}
          feature={feature}
          onLoad={handleOnLoad}
          onError={handleOnError}
          onSuccess={() => {
            wasSucccess = true;
          }}
        />
      ),
      showConfirmButton: true,
      showDenyButton: true,
      showLoaderOnConfirm: true,
      showLoaderOnDeny: true,
      preConfirm: () => preConfirm({ prepareOnPreConfirm }),
      preDeny: () => preDeny({ showBackButton, prepareOnPreDeny }),
      confirmButtonText: (
        <span ref={confirmButtonTextRef}>{t('payment-card-modal.pay')}</span>
      ),
      denyButtonText: t('back'),
      didOpen,
      didDestroy,
    });

    if (wasSucccess) {
      result.isConfirmed = true;
      result.isDismissed = false;
    }

    return result;
  }

  const preConfirm = async ({ prepareOnPreConfirm }) => {
    setLoading(true);
    isUnderErrorRef.current = false;

    try {
      await chargeRef.current();

      if (!isUnderErrorRef.current) {
        await refreshPaymentCard({
          payment: paymentRef.current,
          orders: ordersRef.current,
        });

        const response = await findSpotOrdersFromRemote();
        setOrders(response.data);

        await prepareOnPreConfirm?.call(this);
      }
    } catch (error) {
      handleErrorOnModal(error);
      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  };

  async function preDeny({ showBackButton, prepareOnPreDeny }) {
    setLoading(true);

    try {
      if (showBackButton) {
        return true;
      }

      await cancelPaymentCard(paymentRef.current);
      await prepareOnPreDeny?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  }

  return {
    prepareNewPaymentCardModal: prepare,
    showNewPaymentCardModal: show,
  };
}

export { useNewPaymentCardModal };
